// V4 — Mobile-first shell. Phone-shaped chrome at every viewport:
//   • Compact top app-bar (logo + page title + team switcher + menu)
//   • Single-column scrollable main content
//   • Fixed bottom tab navigation (5 primary destinations)
//   • Slide-up "More" sheet for secondary destinations + theme/sign-out
//
// Logic / data flow mirrors Shell.jsx — same loadState bootstrap,
// same Module dispatch, same setUser plumbing — only the chrome
// changes. Existing views render unmodified; the page-level mobile
// CSS we built in Phase 5 (card-mode tables, etc.) does the rest.

import React, { useCallback, useEffect, useState, useMemo } from "react";
import {
  Home, CalendarDays, Receipt, Trophy, Menu, X, Search,
  LogOut, Sun, Moon, ChevronRight, Shield
} from "lucide-react";
import { loadState, logout } from "../api";
import { nav, NAV_GROUPS, can } from "../nav";
import Module from "./Module";
import { useTheme } from "./Theme";
import { useVersion } from "./Version";
import { ActiveTeamProvider } from "./ActiveTeam";
import TeamSwitcher from "./TeamSwitcher";
import { useUnreadMessages } from "./useUnreadMessages";
import NotificationBell from "./NotificationBell";
import ChatFab from "./ChatFab";

const EMPTY_STATE = {
  members: [], groups: [], teams: [], matches: [], availability: [],
  squads: [], announcements: [], transactions: [], notifications: []
};

// The five tabs pinned at the bottom. "more" launches the secondary
// sheet that exposes every other nav destination.
const PRIMARY_TABS = [
  { key: "dashboard", label: "Home",     icon: Home },
  { key: "schedule",  label: "Schedule", icon: CalendarDays },
  { key: "splits",    label: "Splits",   icon: Receipt },
  { key: "squad",     label: "Squads",   icon: Shield },
  { key: "__more",    label: "More",     icon: Menu }
];

export default function ShellV4({ user, setUser }) {
  const [state, setState]   = useState(EMPTY_STATE);
  const [active, setActive] = useState("dashboard");
  // Optional payload carried alongside a navigation (see Shell.jsx).
  const [navArg, setNavArg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [moreOpen, setMoreOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { version, setVersion } = useVersion();
  // Polls every 15s for new direct messages; toasts when count rises.
  const unreadMessages = useUnreadMessages({
    active,
    onNavigate: setActive
  });

  const reload = useCallback(async () => {
    setError("");
    try {
      const next = await loadState();
      setState(next);
    } catch (err) {
      if (err.status === 401) { setUser(null); return; }
      setError(err.message || "Failed to load data");
    }
  }, [setUser]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const next = await loadState();
        if (alive) setState(next);
      } catch (err) {
        if (!alive) return;
        if (err.status === 401) { setUser(null); return; }
        setError(err.message || "Failed to load data");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [setUser]);

  function signOut() { logout(); setUser(null); }

  // Filter the global nav by the user's roles, then group by the
  // existing NAV_GROUPS sections so the "More" sheet matches the
  // sidebar layout in V1.
  // can(user, key) — user is the user OBJECT (not just .roles).
  // Each nav item declares its `group`; NAV_GROUPS only carries the
  // section { key, label } pair.
  const accessibleNav = useMemo(
    () => nav.filter(item => !item.desktopOnly && can(user, item.key)),
    [user]
  );
  const groupedNav = useMemo(() => {
    return NAV_GROUPS
      .map(g => ({ ...g, items: accessibleNav.filter(n => n.group === g.key) }))
      .filter(g => g.items.length > 0);
  }, [accessibleNav]);

  const currentLabel = nav.find(n => n.key === active)?.label || "";

  function navigateTo(key, arg = null) {
    setActive(key);
    setNavArg(arg);
    setMoreOpen(false);
  }

  return (
    <ActiveTeamProvider user={user} teams={state.teams}>
      <div className="v4-shell">
        {/* ─── TOP APP BAR ─────────────────────────────────────── */}
        <header className="v4-appbar">
          <div className="v4-appbar-brand">
            <img src="/gabruz-brand.png" alt="Gabruz Universe" onError={e => e.currentTarget.style.display = "none"}/>
            <div>
              <small>GABRUZ</small>
              <strong>{currentLabel}</strong>
            </div>
          </div>
          <NotificationBell notifications={state.notifications} onNavigate={navigateTo} compact/>
          <div className="v4-appbar-actions">
            <TeamSwitcher />
            <button
              type="button"
              className="v4-iconBtn"
              onClick={() => setMoreOpen(true)}
              aria-label="Open menu"
            ><Menu size={20}/></button>
          </div>
        </header>

        {/* ─── SCROLLABLE MAIN ────────────────────────────────── */}
        <main className="v4-main">
          {loading ? (
            <div className="empty"><h2>Loading…</h2><p>Fetching data.</p></div>
          ) : error ? (
            <div className="empty">
              <h2>Couldn't load data</h2>
              <p>{error}</p>
              <button className="primary" onClick={reload}>Retry</button>
            </div>
          ) : (
            <Module
              active={active}
              setActive={navigateTo}
              navArg={navArg}
              state={state}
              reload={reload}
              user={user}
              setUser={setUser}
            />
          )}
        </main>

        {/* ─── BOTTOM TAB BAR ─────────────────────────────────── */}
        <nav className="v4-tabbar" aria-label="Primary">
          {PRIMARY_TABS.map(t => {
            const Icon = t.icon;
            const isMore = t.key === "__more";
            const isActive = isMore ? moreOpen : active === t.key;
            // The More tab carries the unread-messages badge since
            // Messages lives inside the More sheet on V4.
            const badge = isMore && unreadMessages > 0 ? unreadMessages : null;
            return (
              <button
                key={t.key}
                type="button"
                className={isActive ? "v4-tab v4-tab--active" : "v4-tab"}
                onClick={() => isMore ? setMoreOpen(true) : navigateTo(t.key)}
                aria-current={isActive ? "page" : undefined}
              >
                <span className="v4-tab-iconWrap">
                  <Icon size={20}/>
                  {badge && (
                    <span className="v4-tab-badge" aria-label={`${badge} unread messages`}>
                      {badge > 99 ? "99+" : badge}
                    </span>
                  )}
                </span>
                <span>{t.label}</span>
              </button>
            );
          })}
        </nav>

        <ChatFab active={active} unread={unreadMessages} onOpen={() => setActive("messages")} />

        {/* ─── MORE SHEET (bottom drawer for secondary nav) ───── */}
        {moreOpen && (
          <>
            <div className="v4-sheetBackdrop" onClick={() => setMoreOpen(false)}/>
            <aside className="v4-sheet" aria-label="More navigation">
              <div className="v4-sheet-handle" aria-hidden="true"/>
              <div className="v4-sheet-head">
                <div className="v4-sheet-user">
                  <div className="avatar">{user.avatar}</div>
                  <div>
                    <strong>{user.name}</strong>
                    <small>
                      <Shield size={11}/>
                      {(user.roles || [])[0]?.replace("_", " ")}
                    </small>
                  </div>
                </div>
                <button
                  type="button"
                  className="v4-iconBtn"
                  onClick={() => setMoreOpen(false)}
                  aria-label="Close menu"
                ><X size={20}/></button>
              </div>

              {/* All nav groups, scrolled if needed */}
              <div className="v4-sheet-body">
                {groupedNav.map(group => (
                  <div key={group.key} className="v4-sheet-group">
                    <h4>{group.label}</h4>
                    <ul>
                      {group.items.map(item => {
                        const Icon = item.icon;
                        const itemBadge = item.key === "messages" && unreadMessages > 0
                          ? unreadMessages : null;
                        return (
                          <li key={item.key}>
                            <button
                              type="button"
                              className={item.key === active ? "v4-sheet-link v4-sheet-link--active" : "v4-sheet-link"}
                              onClick={() => navigateTo(item.key)}
                            >
                              <Icon size={16}/>
                              <span>{item.label}</span>
                              {itemBadge && (
                                <span className="v4-sheet-link-badge">
                                  {itemBadge > 99 ? "99+" : itemBadge}
                                </span>
                              )}
                              <ChevronRight size={14} className="v4-sheet-link-chev"/>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}

                {/* Settings — theme + version + sign out */}
                <div className="v4-sheet-group">
                  <h4>Settings</h4>
                  <ul>
                    <li>
                      <button type="button" className="v4-sheet-link" onClick={toggleTheme}>
                        {theme === "light" ? <Moon size={16}/> : <Sun size={16}/>}
                        <span>Switch to {theme === "light" ? "dark" : "light"} theme</span>
                      </button>
                    </li>
                    <li className="v4-sheet-versionRow">
                      <span>Design version</span>
                      <div className="v4-versionGroup" role="radiogroup" aria-label="Design version">
                        {["v1","v2","v3","v4"].map(v => (
                          <button
                            key={v}
                            type="button"
                            className={version === v ? "v4-versionBtn v4-versionBtn--active" : "v4-versionBtn"}
                            onClick={() => setVersion(v)}
                            role="radio"
                            aria-checked={version === v}
                          >{v.toUpperCase()}</button>
                        ))}
                      </div>
                    </li>
                    <li>
                      <button type="button" className="v4-sheet-link v4-sheet-link--danger" onClick={signOut}>
                        <LogOut size={16}/>
                        <span>Sign out</span>
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </aside>
          </>
        )}
      </div>
    </ActiveTeamProvider>
  );
}
